import {RsnDialog} from '@raison/ui-kit';
import React, {ReactNode, useState} from 'react';
import {DeepMap} from './utils/DeepMap';

export enum GateStep {
    Entry = 'entry',
    Idle = 'idle',
    Exit = 'exit',
}

export type GateProps = {
    pass: () => ReactNode;
    deny: () => void;
    step: GateStep;
};

export interface GateComponent {
    (props: GateProps): ReactNode;
    displayName?: string;
}

interface MergedGateComponent extends GateComponent {
    [MergedGateSources]: GateComponent[];
}

const mergedGatesMemo = new DeepMap<GateComponent, GateComponent>();
export function mergeGates(...gates: GateComponent[]) {
    gates = gates
        .flatMap(gate => {
            if (isMergedGate(gate)) {
                return gate[MergedGateSources];
            } else {
                return gate;
            }
        })
        .filter(gate => gate !== DefaultGate);

    if (gates.length === 0) {
        return DefaultGate;
    }
    if (gates.length === 1) {
        return gates[0];
    }

    const memoized = mergedGatesMemo.get(gates);
    if (memoized) {
        return memoized;
    }

    const [TopGate, ...descendantGates] = gates;

    const MergedGate: MergedGateComponent = ({pass, ...gateApi}) => {
        const finalPass = descendantGates.reduceRight((pass, UpperGate) => {
            // eslint-disable-next-line react/display-name
            return () => <UpperGate {...gateApi} pass={pass} />;
        }, pass);

        return <TopGate {...gateApi} pass={finalPass} />;
    };
    MergedGate[MergedGateSources] = gates;

    {
        mergedGatesMemo.set(gates, MergedGate);
    }

    return MergedGate;
}

const MergedGateSources = Symbol();
function isMergedGate(gate: GateComponent): gate is MergedGateComponent {
    return MergedGateSources in gate;
}

export function DefaultGate({pass}: GateProps): ReactNode {
    return pass();
}

export function GateDialogController({deny, children}: {deny: () => void; children: React.JSX.Element}) {
    const [isClosing, setClosing] = useState(false);

    return (
        <RsnDialog.Controller
            isOpen={!isClosing}
            onClose={async ({exitDone}) => {
                setClosing(true);
                await exitDone;
                deny();
            }}
        >
            {children}
        </RsnDialog.Controller>
    );
}
